import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

/**
 * IrrigationForm - formulario para registro de riego / fertilización
 * Campos respetando el modelo:
 * - date (date)
 * - farmId (fk)
 * - lotId (fk)
 * - applicationType (enum)
 * - performedById (fk)
 * - recipeId (fk)
 * - prescribingAgronomistId (fk)
 * - applicationUnits (number)
 * - recipeDetailsTable (table JSON/text)
 * - applicationUnit (enum: Liter, CropUnit)
 * - photoEvidence (array as CSV/text)
 * - createdAt (YYYY-MM-DD)
 *
 * No validaciones ni uploads reales. Selects con 5 ítems falsos.
 */

const APPLICATION_TYPES = [
  "",
  "Phytosanitary",
  "Nutrition",
  "HerbicideApplication",
  "Other",
];
const APPLICATION_UNITS = ["", "Liter", "CropUnit"];

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

const FAKE_LOTS_BY_FARM = {
  "farm-001": [
    { id: "lot-001A", name: "Lote A" },
    { id: "lot-001B", name: "Lote B" },
    { id: "lot-001C", name: "Lote C" },
    { id: "lot-001D", name: "Lote D" },
    { id: "lot-001E", name: "Lote E" },
  ],
  "farm-002": [
    { id: "lot-002A", name: "Lote A" },
    { id: "lot-002B", name: "Lote B" },
    { id: "lot-002C", name: "Lote C" },
    { id: "lot-002D", name: "Lote D" },
    { id: "lot-002E", name: "Lote E" },
  ],
};

const FAKE_RECIPES = [
  { id: "rx-0001", name: "Receta A - Fitosanitario" },
  { id: "rx-0002", name: "Receta B - Fertilización NPK" },
  { id: "rx-0003", name: "Receta C - Bioinput" },
  { id: "rx-0004", name: "Receta D - Complementaria" },
  { id: "rx-0005", name: "Receta E - Prueba" },
];

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const IrrigationForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    farmId: "",
    farmName: "",
    lotId: "",
    lotName: "",
    applicationType: "",
    performedById: "",
    performedByName: "",
    recipeId: "",
    recipeName: "",
    prescribingAgronomistId: "",
    applicationUnits: "",
    recipeDetailsTable: "", // JSON/text array: [{inputId, availableInventory, unit, quantityToUse}, ...]
    applicationUnit: "",
    photoEvidence: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableLots, setAvailableLots] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });

    if (initialValues.farmId) {
      const lots = FAKE_LOTS_BY_FARM[initialValues.farmId] || [];
      setAvailableLots(lots);
      if (initialValues.lotId && !initialValues.lotName) {
        const foundLot = lots.find((l) => l.id === initialValues.lotId);
        if (foundLot) setForm((f) => ({ ...f, lotName: foundLot.name }));
      }
    }
    if (initialValues.recipeId && !initialValues.recipeName) {
      const found = FAKE_RECIPES.find((r) => r.id === initialValues.recipeId);
      if (found) setForm((f) => ({ ...f, recipeName: found.name }));
    }
    if (initialValues.performedById && !initialValues.performedByName) {
      const found = FAKE_COLLABORATORS.find(
        (c) => c.id === initialValues.performedById
      );
      if (found) setForm((f) => ({ ...f, performedByName: found.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFarmChange = (farmId) => {
    const farm = FAKE_FARMS.find((f) => f.id === farmId);
    const lots = FAKE_LOTS_BY_FARM[farmId] || [];
    setAvailableLots(lots);
    setForm({
      ...form,
      farmId,
      farmName: farm ? farm.name : "",
      lotId: "",
      lotName: "",
    });
  };

  const handleLotChange = (lotId) => {
    const lot = (availableLots || []).find((l) => l.id === lotId);
    setForm({ ...form, lotId, lotName: lot ? lot.name : "" });
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    if (!payload.createdAt)
      payload.createdAt = new Date().toISOString().slice(0, 10);
    // try parse recipeDetailsTable string to JSON if provided
    try {
      if (
        typeof payload.recipeDetailsTable === "string" &&
        payload.recipeDetailsTable.trim()
      ) {
        payload.recipeDetailsTable = JSON.parse(payload.recipeDetailsTable);
      }
    } catch (e) {
      // leave as string if invalid JSON
    }
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => handleFarmChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="lotId"
          label={strings.fields.lotId.label}
          value={form.lotId || ""}
          onChange={(e) => handleLotChange(e.target.value)}
        >
          <option value="">--</option>
          {availableLots.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="applicationType"
          label={strings.fields.applicationType.label}
          value={form.applicationType || ""}
          onChange={(e) =>
            setForm({ ...form, applicationType: e.target.value })
          }
        >
          {APPLICATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t || "--"}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_COLLABORATORS.find((c) => c.id === val);
            setForm({
              ...form,
              performedById: val,
              performedByName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="recipeId"
          label={strings.fields.recipeId.label}
          value={form.recipeId || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_RECIPES.find((r) => r.id === val);
            setForm({
              ...form,
              recipeId: val,
              recipeName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_RECIPES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="prescribingAgronomistId"
          label={strings.fields.prescribingAgronomistId.label}
          value={form.prescribingAgronomistId || ""}
          onChange={(e) =>
            setForm({ ...form, prescribingAgronomistId: e.target.value })
          }
        />
        <TextField
          name="applicationUnits"
          label={strings.fields.applicationUnits.label}
          value={form.applicationUnits || ""}
          onChange={(e) =>
            setForm({ ...form, applicationUnits: e.target.value })
          }
        />
      </Grid>

      <TextAreaField
        name="recipeDetailsTable"
        label={strings.fields.recipeDetailsTable.label}
        placeholder='Ej: [{"inputId":"in-0001","availableInventory":120,"unit":"Litro","quantityToUse":2}, ...]'
        value={form.recipeDetailsTable || ""}
        onChange={(e) =>
          setForm({ ...form, recipeDetailsTable: e.target.value })
        }
      />

      <SelectField
        name="applicationUnit"
        label={strings.fields.applicationUnit.label}
        value={form.applicationUnit || ""}
        onChange={(e) => setForm({ ...form, applicationUnit: e.target.value })}
      >
        {APPLICATION_UNITS.map((a) => (
          <option key={a} value={a}>
            {a || "--"}
          </option>
        ))}
      </SelectField>

      <TextField
        name="photoEvidence"
        label={strings.fields.photoEvidence.label}
        placeholder="foto1.jpg, foto2.jpg"
        value={
          Array.isArray(form.photoEvidence)
            ? form.photoEvidence.join(", ")
            : form.photoEvidence || ""
        }
        onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha de riego / fertilización" },
      farmId: { label: "Finca" },
      lotId: { label: "Lote" },
      applicationType: { label: "Tipo" },
      performedById: { label: "Persona que realiza" },
      recipeId: { label: "Receta" },
      prescribingAgronomistId: { label: "Agrónomo que receta" },
      applicationUnits: { label: "Cantidad de unidades de aplicación" },
      recipeDetailsTable: { label: "Detalle receta" },
      applicationUnit: { label: "Unidad de aplicación" },
      photoEvidence: { label: "Evidencia fotográfica" },
      createdAt: { label: "Fecha de creación" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default IrrigationForm;
